























































































































































































































import Vue from 'vue';
import { Expression, Types } from 'expangine-runtime';
import { Debugger, DebugStep, DebugBreakpoint } from './Debugger';
import { ExpressionEventListeners } from '../runtime/exprs/ExpressionBase';
import { TypeSubNode } from '../runtime/types/TypeVisuals';
import { Registry } from '../runtime/Registry';
import ExDebugStack from './DebugStack.vue';
import ExDebugNode from './DebugNode.vue';
import ExDebugStep from './DebugStep.vue';
import ExDebugBreakpoint from './DebugBreakpoint.vue';
import { Preferences, PreferenceCategory } from './Preference';



const PREF_ORIENTATION = Preferences.define({
  key: 'expression_debug_orientation',
  label: 'Debugger vertical toolbar orientation',
  category: [PreferenceCategory.DEBUGGER],
  defaultValue: null as null | boolean,
  type: Types.bool(),
});

export default Vue.extend({
  components: {
    ExDebugStack,
    ExDebugNode,
    ExDebugStep,
    ExDebugBreakpoint,
  },
  props: {
    debug: {
      type: Object as () => Debugger,
      default: null,
    },
    registry: {
      type: Object as () => Registry,
      required: true,
    },
    hideClose: {
      type: Boolean,
      default: false,
    },
    vertical: {
      type: Boolean,
      default: false,
    },
    sticky: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    hoverExpression: null as null | Expression,
    stepsLoaded: 0,
    breakpointSet: false,
    breakpoint: null as null | Expression,
    topOffset: 0,
    stickyEnabled: true,
    internalVertical: Preferences.get(PREF_ORIENTATION),
  }),
  computed: {
    step(): DebugStep {
      return this.debug.current.stepDebug;
    },
    stepName(): string {
      return this.registry.getExpressionName(this.step.expr);
    },
    breakpoints(): DebugBreakpoint[] {
      return Array.from(this.debug.breakpoints.entries());
    },
    highlightMap(): Map<Expression, string> {
      const highlights = new Map();
      
      if (this.breakpoint) {
        highlights.set(this.breakpoint, '#9CCC65');
      } else {
        this.breakpoints.forEach(([breakpoint, enabled]) => {
          if (enabled) {
            highlights.set(breakpoint, '#FFCDD2');
          }
        });
      }

      if (this.hoverExpression) {
        highlights.set(this.hoverExpression, '#DDF0FFC4');
      }

      if (!this.breakpointSet) {
        if (highlights.has(this.step.expr)) {
          highlights.set(this.step.expr, '#E1BEE7');
        } else {
          highlights.set(this.step.expr, '#BBDEFB');
        }
      }

      return highlights;
    },
    callstack(): DebugStep[] {
      return this.debug.getCallstack();
    },
    scopeNodes(): TypeSubNode[] {
      return this.debug.getScopeNodes();
    },
    disabledBack(): boolean {
      return this.debug.current.step === 0;
    },
    disabledForward(): boolean {
      return this.debug.current.end;
    },
    stepsVisible(): DebugStep[] {
      return this.debug.steps.slice(0, this.stepsLoaded);
    },
    stepBackLabel(): string {
      const step = this.getStepName(this.debug.current.stepBack);

      return step ? ' to ' + step : '';
    },
    stepIntoLabel(): string {
      return this.stepName;
    },
    stepOverLabel(): string {
      return this.stepName;
    },
    stepOutLabel(): string {
      const step = this.getStepName(this.debug.current.stepBack);

      return step ? ' of ' + step : '';
    },
    eventListeners(): ExpressionEventListeners {
      return { click: this.gotoClick };
    },
    breakpointMap(): Map<Expression, boolean> {
      return new Map(this.breakpoints);
    },
    isVertical(): boolean {
      return this.internalVertical !== null
        ? this.internalVertical 
        : this.vertical;
    },
    verticalIcon(): string {
      return this.isVertical ? 'mdi-arrow-right' : 'mdi-arrow-down';
    },
    verticalText(): string {
      return this.isVertical ? 'Switch to Horizontal' : 'Switch to Vertical';
    },
    stickyIcon(): string {
      return this.vertical
        ? 'mdi-arrow-up-down'
        : 'mdi-arrow-left-right';
    },
    stickyText(): string {
      return this.stickyEnabled ? 'Disable Auto Scrolling' : 'Enable Auto Scrolling';
    },
    stickyTarget(): string | false {
      return this.sticky && this.stickyEnabled ? '.v-toolbar__content' : false;
    },
    classes(): any {
      return {
        vertical: this.isVertical,
      };
    },
    elapsedText(): string {
      return this.isVertical ? this.debug.elapsed.elapsedShort : this.elapsedTitle;
    },
    elapsedTitle(): string {
      return `Stepped in ${this.debug.elapsed.elapsedSecondsFormatted}s`;
    },
  },
  methods: {
    toggleVertical() {
      this.internalVertical = !this.isVertical;

      Preferences.set(PREF_ORIENTATION, this.internalVertical);
    },
    updateTopOffset() {
      const rect = this.$el.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

      this.topOffset = rect.top + scrollTop;
    },
    gotoClick(ev: Event, expr: Expression) {
      if (this.breakpointSet) {
        this.breakpoint = expr;

        ev.stopPropagation();
        ev.preventDefault();
      }
    },
    toggleSticky() {
      this.stickyEnabled = !this.stickyEnabled;
    },
    gotoStart() {
      this.breakpointSet = true;
      this.breakpoint = null;
    },
    gotoEnd() {
      if (this.breakpoint) {
        this.debug.addBreakpoint(this.breakpoint);
      }
      this.gotoCancel();
    },
    gotoCancel() {
      this.breakpointSet = false;
      this.breakpoint = null;
    },
    getStep(index: number): DebugStep | undefined {
      return this.debug.steps.find((s) => s.index === index);
    },
    getStepName(index: number): string {
      const step = this.getStep(index);

      return step ? this.registry.getExpressionName(step.expr) : '';
    },
    removeBreakpoint(expr: Expression) {
      this.debug.removeBreakpoint(expr);
    },
    close() {
      this.$emit('close');
    },
    first() {
      this.debug.first();
    },
    stepBack() {
      this.debug.stepBack();
    },
    stepInto() {
      this.debug.stepInto();
    },
    stepOver() {
      this.debug.stepOver();
    },
    stepOut() {
      this.debug.stepOut();
    },
    stepToBreakpoint() {
      this.debug.stepToBreakpoint();
    },
    last() {
      this.debug.last();
    },
    onHoverStep(step: DebugStep | null = null) {
      this.hoverExpression = step ? step.expr : null;
    },
    onHoverBreakpoint(breakpoint: DebugBreakpoint | null = null) {
      this.hoverExpression = breakpoint ? breakpoint[0] : null;
    },
    onIntersect(entries: Array<{ isIntersecting: boolean }>) {
      if (entries[0].isIntersecting && this.stepsLoaded < this.debug.steps.length) {
        this.stepsLoaded = Math.min(this.debug.steps.length, this.stepsLoaded + 10);
      }
    },
  },
});
